import { Point, Position } from './constants';

export function calculateLeftAndTopDiff(
  anchor: HTMLElement,
  popover: HTMLElement,
  signpostPosition: Position
) {
  const { anchorPoint, popoverPoint, offsetX, offsetY } = signpostPosition;

  const anchorRect = anchor.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();

  // position of left top corner of anchor + the offset
  let leftDiff = anchorRect.left - popoverRect.left + offsetX;
  let topDiff = anchorRect.top - popoverRect.top + offsetY;

  // first, adjust positioning based on anchor's align point
  switch (anchorPoint) {
    case Point.LEFT_TOP:
    case Point.TOP_LEFT:
      break;
    case Point.TOP_CENTER:
      leftDiff += anchorRect.width / 2;
      break;
    case Point.TOP_RIGHT:
      leftDiff += anchorRect.width;
      break;
    case Point.RIGHT_TOP:
      leftDiff += anchorRect.width;
      break;
    case Point.LEFT_BOTTOM:
      topDiff += anchorRect.height;
      break;
    case Point.BOTTOM_LEFT:
      topDiff += anchorRect.height;
      break;
    case Point.BOTTOM_CENTER:
      topDiff += anchorRect.height;
      leftDiff += anchorRect.width / 2;
      break;
    case Point.BOTTOM_RIGHT:
      topDiff += anchorRect.height;
      leftDiff += anchorRect.width;
      break;
    case Point.RIGHT_BOTTOM:
      topDiff += anchorRect.height;
      leftDiff += anchorRect.width;
      break;
    case Point.LEFT_CENTER:
      topDiff += anchorRect.height / 2;
      break;
    case Point.RIGHT_CENTER:
      topDiff += anchorRect.height / 2;
      leftDiff += anchorRect.width;
      break;
    default:
  }

  // second, adjust positioning based on popover's align point
  switch (popoverPoint) {
    case Point.LEFT_TOP:
    case Point.TOP_LEFT:
      break;
    case Point.TOP_CENTER:
      leftDiff -= popoverRect.width / 2;
      break;
    case Point.TOP_RIGHT:
      leftDiff -= popoverRect.width;
      break;
    case Point.RIGHT_TOP:
      leftDiff -= popoverRect.width;
      break;
    case Point.LEFT_BOTTOM:
      topDiff -= popoverRect.height;
      break;
    case Point.BOTTOM_LEFT:
      topDiff -= popoverRect.height;
      break;
    case Point.BOTTOM_CENTER:
      topDiff -= popoverRect.height;
      leftDiff -= popoverRect.width / 2;
      break;
    case Point.BOTTOM_RIGHT:
      topDiff -= popoverRect.height;
      leftDiff -= popoverRect.width;
      break;
    case Point.RIGHT_BOTTOM:
      topDiff -= popoverRect.height;
      leftDiff -= popoverRect.width;
      break;
    case Point.LEFT_CENTER:
      topDiff -= popoverRect.height / 2;
      break;
    case Point.RIGHT_CENTER:
      topDiff -= popoverRect.height / 2;
      leftDiff -= popoverRect.width;
      break;
    default:
  }

  // Third, adjust with popover's margins based on the two align points.
  // Here, we make an assumption that popover is primarily positioned outside the
  // anchor with minor offset. Without this assumption, it's impossible to apply
  // the popover's margins in a predictable way. For example, assume that a popover
  // and its anchor are exactly the same size. if a popover is positioned inside the
  // anchor (which is technically possible), then it becomes impossible to know what to do
  // if the popover has a non-zero margin value all around (because applying the margin in
  // all four directions will result in no margin visually, which isn't what we want).
  // Therefore, our logic makes assumptions about margins of interest given the points,
  // and only covers the cases where popover is outside the anchor.

  const popoverComputedStyle = getComputedStyle(popover);
  const marginLeft = parseInt(popoverComputedStyle.marginLeft, 10);
  const marginRight = parseInt(popoverComputedStyle.marginRight, 10);
  const marginTop = parseInt(popoverComputedStyle.marginTop, 10);
  const marginBottom = parseInt(popoverComputedStyle.marginBottom, 10);

  switch (anchorPoint) {
    case Point.LEFT_TOP:
    case Point.TOP_LEFT:
    case Point.TOP_RIGHT:
    case Point.RIGHT_TOP:
      if (
        popoverPoint === Point.BOTTOM_RIGHT ||
        popoverPoint === Point.RIGHT_BOTTOM
      ) {
        topDiff -= marginBottom;
        leftDiff -= marginRight;
      }
      if (
        popoverPoint === Point.BOTTOM_LEFT ||
        popoverPoint === Point.LEFT_BOTTOM
      ) {
        topDiff -= marginTop;
        leftDiff += marginLeft;
      }
      if (popoverPoint === Point.TOP_LEFT || popoverPoint === Point.LEFT_TOP) {
        topDiff += marginTop;
        leftDiff += marginLeft;
      }
      if (
        popoverPoint === Point.TOP_RIGHT ||
        popoverPoint === Point.RIGHT_TOP
      ) {
        topDiff += marginTop;
        leftDiff -= marginRight;
      }
      break;
    case Point.LEFT_BOTTOM:
    case Point.BOTTOM_LEFT:
    case Point.BOTTOM_RIGHT:
    case Point.RIGHT_BOTTOM:
      if (
        popoverPoint === Point.BOTTOM_LEFT ||
        popoverPoint === Point.LEFT_BOTTOM
      ) {
        topDiff -= marginBottom;
        leftDiff += marginLeft;
      }
      if (
        popoverPoint === Point.BOTTOM_RIGHT ||
        popoverPoint === Point.RIGHT_BOTTOM
      ) {
        topDiff -= marginBottom;
        leftDiff -= marginRight;
      }
      if (popoverPoint === Point.TOP_LEFT || popoverPoint === Point.LEFT_TOP) {
        topDiff += marginTop;
        leftDiff += marginLeft;
      }
      if (
        popoverPoint === Point.TOP_RIGHT ||
        popoverPoint === Point.RIGHT_TOP
      ) {
        topDiff += marginTop;
        leftDiff -= marginRight;
      }
      break;
    case Point.TOP_CENTER:
      topDiff -= marginBottom;
      leftDiff += marginLeft;
      leftDiff -= marginRight;
      break;
    case Point.BOTTOM_CENTER:
      topDiff += marginTop;
      leftDiff += marginLeft;
      leftDiff -= marginRight;
      break;
    case Point.LEFT_CENTER:
      topDiff += marginTop;
      topDiff -= marginBottom;
      leftDiff -= marginRight;
      break;
    case Point.RIGHT_CENTER:
      topDiff += marginTop;
      topDiff -= marginBottom;
      leftDiff += marginLeft;
      break;
    default:
  }

  return { leftDiff, topDiff };
}
